import { httpsCallable } from 'firebase/functions';
import { getFirebaseFunctions } from './firebase';
import type {
  SessionType,
  SessionModality,
  SessionOutputs,
  RealTimeExtraction,
  ContinuityBrief,
  VoiceProvider,
} from '../types';

// ─── Request / Response Shapes ────────────────────────────────────────────────
// These mirror the handler interfaces in functions/src/handlers/

interface StartSessionRequest {
  sessionType: SessionType;
  modality: SessionModality;
}

interface StartSessionResponse {
  sessionId: string;
  openingMessage: string;
  continuityBrief: ContinuityBrief;
}

interface SendMessageRequest {
  sessionId: string;
  message: string;
}

interface SendMessageResponse {
  assistantMessage: string;
  messageIndex: number;
  realtimeExtractions: RealTimeExtraction[];
}

interface ProcessExtractionRequest {
  sessionId: string;
  outputs: SessionOutputs;
}

interface ProcessExtractionResponse {
  nodesCreated: number;
  nodesReinforced: number;
  edgesCreated: number;
  motifsIdentified: number;
  contradictionsFound: number;
  updatedNodeCount: number;
  updatedEdgeCount: number;
}

interface GenerateQuickPlanRequest {
  instruction: string;
  inputMode?: 'text' | 'voice';
}

export interface GenerateQuickPlanResponse {
  taskId: string;
  title: string;
  summary: string;
  actionItems: string[];
  plannerModel: string;
}

interface GetVoiceTokenRequest {
  sessionType: SessionType;
  provider?: VoiceProvider;
  continueSessionId?: string;
}

interface GetVoiceTokenResponse {
  ephemeralToken: string;
  sessionId: string;
  systemPrompt: string;
  expiresAt: number;
  provider: VoiceProvider;
  model: string;
  voice: string;
}

interface SynthesizeSpeechRequest {
  text: string;
  voiceId?: string;
  modelId?: string;
  sessionId?: string;
}

interface SynthesizeSpeechResponse {
  audioBase64: string;
  sampleRate: number;
}

interface RecordVoiceUsageRequest {
  sessionId: string;
  provider: VoiceProvider;
  model?: string;
  durationSeconds?: number;
  reportId?: string;
  usage?: {
    inputTextTokens?: number;
    outputTextTokens?: number;
    inputAudioTokens?: number;
    outputAudioTokens?: number;
    transcriptionAudioInputTokens?: number;
    transcriptionModel?: string;
  };
}

interface RecordVoiceUsageResponse {
  recorded: boolean;
  eventCount: number;
  reportId: string;
}

interface InitiatePhoneCallRequest {
  sessionType: SessionType;
}

interface InitiatePhoneCallResponse {
  callSid: string;
  status: string;
}

interface StartPhoneVerificationRequest {
  phoneNumber: string;
}

interface StartPhoneVerificationResponse {
  status: string;
  phoneNumber: string;
}

interface CheckPhoneVerificationRequest {
  phoneNumber: string;
  code: string;
}

interface CheckPhoneVerificationResponse {
  approved: boolean;
  status: string;
  phoneNumber: string;
}

interface GetBillingDashboardRequest {
  days?: number;
  userId?: string | null;
}

interface GetControlCentreAvailabilityRequest {}

interface RunAgentTaskRequest {
  taskId: string;
}

export interface RunAgentTaskResponse {
  taskId: string;
  jobId: string;
  route: 'api_mcp' | 'browser' | 'voice';
  status: 'completed' | 'failed' | 'needs_confirmation';
}

interface ConfirmBrowserTaskActionsRequest {
  taskId: string;
}

export interface ConfirmBrowserTaskActionsResponse {
  taskId: string;
  confirmed: true;
}

interface ScheduleAgentTaskRequest {
  taskId: string;
  runAtMs?: number;
  delayMinutes?: number;
}

export interface ScheduleAgentTaskResponse {
  taskId: string;
  scheduledAtMs: number;
}

export type ControlCentreGateReason =
  | 'enabled'
  | 'backend_disabled'
  | 'kill_switch_active'
  | 'not_allowlisted'
  | 'user_disabled'
  | 'tier_ineligible';

export interface GetControlCentreAvailabilityResponse {
  available: boolean;
  reason: ControlCentreGateReason;
  gates: {
    backendEnabled: boolean;
    killSwitchActive: boolean;
    allowlisted: boolean;
    userEnabled: boolean;
    tierEligible: boolean;
  };
  tier: {
    userTier: string;
    requiredTier: string | null;
  };
}

export type TextAiProvider = 'anthropic' | 'openai' | 'google';
export type SupportedRouteKind = 'postSession' | 'realtime' | 'coaching';

export interface AiRouteSelection {
  provider: TextAiProvider;
  model: string;
  label: string;
}

export interface TextModelPriceEntry {
  provider: TextAiProvider;
  model: string;
  label: string;
  supportedRoutes: SupportedRouteKind[];
  inputUsdPerMillion: number;
  outputUsdPerMillion: number;
}

export interface AiRuntimeConfig {
  source: 'default' | 'firestore';
  coaching: {
    textModel: AiRouteSelection;
  };
  extraction: {
    postSession: AiRouteSelection;
    realtime: AiRouteSelection;
  };
  pricingCatalog: {
    version: string;
    effectiveAtMs: number;
    models: TextModelPriceEntry[];
  };
  remoteConfigMirror: {
    enabled: boolean;
  };
  updatedAtMs: number | null;
  updatedBy: string | null;
}

export interface UpdateAiRuntimeConfigRequest {
  extraction: {
    postSession: { provider: TextAiProvider; model: string };
    realtime: { provider: TextAiProvider; model: string };
  };
  pricingCatalog: {
    models: Array<{
      provider: TextAiProvider;
      model: string;
      inputUsdPerMillion: number;
      outputUsdPerMillion: number;
    }>;
  };
  remoteConfigMirror?: {
    enabled?: boolean;
  };
}

export interface BillingGroupRow {
  key: string;
  label: string;
  costMicros: number;
  eventCount: number;
  share: number;
}

export interface BillingUserSummary {
  userId: string;
  displayName: string;
  email: string;
  costMicros: number;
  eventCount: number;
}

export interface BillingDailyPoint {
  date: string;
  costMicros: number;
  eventCount: number;
}

export type BillingStackDimension = 'provider' | 'model' | 'service' | 'user' | 'channel' | 'eventType';

export interface BillingStackSegment {
  key: string;
  label: string;
  costMicros: number;
}

export interface BillingStackDay {
  date: string;
  totalCostMicros: number;
  segments: BillingStackSegment[];
}

export interface BillingStackedSeries {
  dimension: BillingStackDimension;
  label: string;
  categories: BillingStackSegment[];
  days: BillingStackDay[];
}

export interface BillingVoiceSummary {
  totalCostMicros: number;
  eventCount: number;
  phoneCostMicros: number;
  webCostMicros: number;
  telephonyCostMicros: number;
  realtimeVoiceCostMicros: number;
  sttCostMicros: number;
  ttsCostMicros: number;
  totalMinutes: number;
  byProvider: BillingGroupRow[];
  byService: BillingGroupRow[];
  byModel: BillingGroupRow[];
  byChannel: BillingGroupRow[];
}

export interface BillingRecentEvent {
  eventId: string;
  eventTimeMs: number;
  userId: string | null;
  userLabel: string;
  eventType: string;
  channel: string;
  provider: string;
  model: string | null;
  sourceService: string;
  sourceFunction: string | null;
  amountStatus: 'estimated' | 'final';
  totalCostMicros: number;
}

export interface BillingDashboardResponse {
  generatedAtMs: number;
  range: {
    days: number;
    startMs: number;
    endMs: number;
    truncated: boolean;
    scannedEventCount: number;
  };
  selectedUserId: string | null;
  totals: {
    totalCostMicros: number;
    estimatedCostMicros: number;
    finalCostMicros: number;
    eventCount: number;
    uniqueUsers: number;
    avgCostPerUserMicros: number;
  };
  users: BillingUserSummary[];
  topProviders: BillingGroupRow[];
  topModels: BillingGroupRow[];
  topServices: BillingGroupRow[];
  topChannels: BillingGroupRow[];
  topEventTypes: BillingGroupRow[];
  daily: BillingDailyPoint[];
  stackedDaily: BillingStackedSeries[];
  voice: BillingVoiceSummary;
  recentEvents: BillingRecentEvent[];
  aiRuntimeConfig: AiRuntimeConfig;
}

// ─── Callable Wrappers ────────────────────────────────────────────────────────

export async function callGetVoiceToken(
  sessionType: SessionType,
  provider?: VoiceProvider,
  continueSessionId?: string
): Promise<GetVoiceTokenResponse> {
  const fn = httpsCallable<GetVoiceTokenRequest, GetVoiceTokenResponse>(
    getFirebaseFunctions(),
    'getVoiceToken'
  );
  const result = await fn({ sessionType, provider, continueSessionId });
  return result.data;
}

export async function callStartSession(
  sessionType: SessionType,
  modality: SessionModality = 'text'
): Promise<StartSessionResponse> {
  const fn = httpsCallable<StartSessionRequest, StartSessionResponse>(
    getFirebaseFunctions(),
    'startSession'
  );
  const result = await fn({ sessionType, modality });
  return result.data;
}

export async function callSendMessage(
  sessionId: string,
  message: string
): Promise<SendMessageResponse> {
  const fn = httpsCallable<SendMessageRequest, SendMessageResponse>(
    getFirebaseFunctions(),
    'sendMessage'
  );
  const result = await fn({ sessionId, message });
  return result.data;
}

export async function callProcessExtraction(
  sessionId: string,
  outputs: SessionOutputs = {}
): Promise<ProcessExtractionResponse> {
  const fn = httpsCallable<ProcessExtractionRequest, ProcessExtractionResponse>(
    getFirebaseFunctions(),
    'processExtraction',
    { timeout: 300000 } // extraction + task plan can take up to 5 minutes
  );
  const result = await fn({ sessionId, outputs });
  return result.data;
}

export async function callGenerateQuickPlan(
  instruction: string,
  inputMode: 'text' | 'voice' = 'text'
): Promise<GenerateQuickPlanResponse> {
  const fn = httpsCallable<GenerateQuickPlanRequest, GenerateQuickPlanResponse>(
    getFirebaseFunctions(),
    'generateQuickPlan',
    { timeout: 120000 }
  );
  const result = await fn({ instruction, inputMode });
  return result.data;
}

export async function callSynthesizeSpeech(
  text: string,
  options: { voiceId?: string; modelId?: string; sessionId?: string } = {}
): Promise<SynthesizeSpeechResponse> {
  const fn = httpsCallable<SynthesizeSpeechRequest, SynthesizeSpeechResponse>(
    getFirebaseFunctions(),
    'synthesizeSpeech',
    { timeout: 30000 }
  );
  const result = await fn({
    text,
    voiceId: options.voiceId,
    modelId: options.modelId,
    sessionId: options.sessionId,
  });
  return result.data;
}

export async function callRecordVoiceUsage(
  input: RecordVoiceUsageRequest
): Promise<RecordVoiceUsageResponse> {
  const fn = httpsCallable<RecordVoiceUsageRequest, RecordVoiceUsageResponse>(
    getFirebaseFunctions(),
    'recordVoiceUsage',
    { timeout: 30000 }
  );
  const result = await fn(input);
  return result.data;
}

export async function callInitiatePhoneCall(
  sessionType: SessionType
): Promise<InitiatePhoneCallResponse> {
  const fn = httpsCallable<InitiatePhoneCallRequest, InitiatePhoneCallResponse>(
    getFirebaseFunctions(),
    'initiatePhoneCall',
    { timeout: 30000 }
  );
  const result = await fn({ sessionType });
  return result.data;
}

export async function callStartPhoneVerification(
  phoneNumber: string
): Promise<StartPhoneVerificationResponse> {
  const fn = httpsCallable<StartPhoneVerificationRequest, StartPhoneVerificationResponse>(
    getFirebaseFunctions(),
    'startPhoneVerification',
    { timeout: 30000 }
  );
  const result = await fn({ phoneNumber });
  return result.data;
}

export async function callCheckPhoneVerification(
  phoneNumber: string,
  code: string
): Promise<CheckPhoneVerificationResponse> {
  const fn = httpsCallable<CheckPhoneVerificationRequest, CheckPhoneVerificationResponse>(
    getFirebaseFunctions(),
    'checkPhoneVerification',
    { timeout: 30000 }
  );
  const result = await fn({ phoneNumber, code });
  return result.data;
}

export async function callGetBillingDashboard(
  input: GetBillingDashboardRequest = {}
): Promise<BillingDashboardResponse> {
  const fn = httpsCallable<GetBillingDashboardRequest, BillingDashboardResponse>(
    getFirebaseFunctions(),
    'getBillingDashboard',
    { timeout: 60000 }
  );
  const result = await fn(input);
  return result.data;
}

export async function callGetControlCentreAvailability(): Promise<GetControlCentreAvailabilityResponse> {
  const fn = httpsCallable<GetControlCentreAvailabilityRequest, GetControlCentreAvailabilityResponse>(
    getFirebaseFunctions(),
    'getControlCentreAvailability',
    { timeout: 10000 }
  );
  const result = await fn({});
  return result.data;
}

export async function callRunAgentTask(taskId: string): Promise<RunAgentTaskResponse> {
  const fn = httpsCallable<RunAgentTaskRequest, RunAgentTaskResponse>(
    getFirebaseFunctions(),
    'runAgentTask',
    { timeout: 120000 }
  );
  const result = await fn({ taskId });
  return result.data;
}

export async function callScheduleAgentTask(
  taskId: string,
  options: { runAtMs?: number; delayMinutes?: number } = {}
): Promise<ScheduleAgentTaskResponse> {
  const fn = httpsCallable<ScheduleAgentTaskRequest, ScheduleAgentTaskResponse>(
    getFirebaseFunctions(),
    'scheduleAgentTask',
    { timeout: 30000 }
  );
  const result = await fn({
    taskId,
    runAtMs: options.runAtMs,
    delayMinutes: options.delayMinutes,
  });
  return result.data;
}

interface SendAgentTaskMessageRequest {
  taskId: string;
  message: string;
}

export interface SendAgentTaskMessageResponse {
  taskId: string;
  agentResponse: string;
  toolCallCount: number;
  estimatedCostUsd: number;
}

export async function callSendAgentTaskMessage(
  taskId: string,
  message: string
): Promise<SendAgentTaskMessageResponse> {
  const fn = httpsCallable<SendAgentTaskMessageRequest, SendAgentTaskMessageResponse>(
    getFirebaseFunctions(),
    'sendAgentTaskMessage',
    { timeout: 300000 }
  );
  const result = await fn({ taskId, message });
  return result.data;
}

export async function callConfirmBrowserTaskActions(taskId: string): Promise<ConfirmBrowserTaskActionsResponse> {
  const fn = httpsCallable<ConfirmBrowserTaskActionsRequest, ConfirmBrowserTaskActionsResponse>(
    getFirebaseFunctions(),
    'confirmBrowserTaskActions',
    { timeout: 30000 }
  );
  const result = await fn({ taskId });
  return result.data;
}

export async function callUpdateAiRuntimeConfig(
  input: UpdateAiRuntimeConfigRequest
): Promise<AiRuntimeConfig> {
  const fn = httpsCallable<UpdateAiRuntimeConfigRequest, AiRuntimeConfig>(
    getFirebaseFunctions(),
    'updateAiRuntimeConfig',
    { timeout: 30000 }
  );
  const result = await fn(input);
  return result.data;
}
